/**
 * Catch Board — frontend stage / filter config for The Bridge kanban.
 * Values must stay aligned with express_app/constants/catchBoard.js.
 */

export const CATCH_BOARD_STAGE = Object.freeze({
  CASTING: 'casting',
  NIBBLES: 'nibbles',
  HOOKED: 'hooked',
  REELING_IN: 'reeling_in',
  LANDED: 'landed',
  GOT_AWAY: 'got_away',
})

/** Column order left → right on the board. */
export const CATCH_BOARD_STAGES = Object.freeze([
  CATCH_BOARD_STAGE.CASTING,
  CATCH_BOARD_STAGE.NIBBLES,
  CATCH_BOARD_STAGE.HOOKED,
  CATCH_BOARD_STAGE.REELING_IN,
  CATCH_BOARD_STAGE.LANDED,
  CATCH_BOARD_STAGE.GOT_AWAY,
])

/**
 * Stage meta for columns and cards.
 * Colors are soft column accents (OMS greens/blues/greys — not purple gradients).
 */
export const CATCH_BOARD_STAGE_META = Object.freeze({
  [CATCH_BOARD_STAGE.CASTING]: {
    key: CATCH_BOARD_STAGE.CASTING,
    label: 'Casting',
    subtitle: 'Leads',
    emoji: '🎣',
    order: 1,
    columnAccent: '#5B8A72',
    columnBackground: '#F4F8F6',
  },
  [CATCH_BOARD_STAGE.NIBBLES]: {
    key: CATCH_BOARD_STAGE.NIBBLES,
    label: 'Nibbles',
    subtitle: 'Responded',
    emoji: '👀',
    order: 2,
    columnAccent: '#4A7C8C',
    columnBackground: '#F3F7F9',
  },
  [CATCH_BOARD_STAGE.HOOKED]: {
    key: CATCH_BOARD_STAGE.HOOKED,
    label: 'Hooked',
    subtitle: 'Interested',
    emoji: '🪝',
    order: 3,
    columnAccent: '#3D6B8A',
    columnBackground: '#F2F6FA',
  },
  [CATCH_BOARD_STAGE.REELING_IN]: {
    key: CATCH_BOARD_STAGE.REELING_IN,
    label: 'Reeling In',
    subtitle: 'Demo scheduled or completed',
    emoji: '🚤',
    order: 4,
    columnAccent: '#2F6B5A',
    columnBackground: '#F1F7F4',
  },
  [CATCH_BOARD_STAGE.LANDED]: {
    key: CATCH_BOARD_STAGE.LANDED,
    label: 'Landed',
    subtitle: 'Business won',
    emoji: '🐟',
    order: 5,
    columnAccent: '#2E7D4F',
    columnBackground: '#F0F7F2',
  },
  [CATCH_BOARD_STAGE.GOT_AWAY]: {
    key: CATCH_BOARD_STAGE.GOT_AWAY,
    label: 'Got Away',
    subtitle: 'Business lost',
    emoji: '❌',
    order: 6,
    columnAccent: '#8A6A5B',
    columnBackground: '#F8F5F3',
  },
})

export const CATCH_BOARD_CLOSED_STAGES = Object.freeze([
  CATCH_BOARD_STAGE.LANDED,
  CATCH_BOARD_STAGE.GOT_AWAY,
])

export const CATCH_BOARD_OPEN_STAGES = Object.freeze(
  CATCH_BOARD_STAGES.filter(stage => !CATCH_BOARD_CLOSED_STAGES.includes(stage)),
)

export const CATCH_BOARD_PRIORITY = Object.freeze({
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
})

export const CATCH_BOARD_PRIORITIES = Object.freeze([
  CATCH_BOARD_PRIORITY.LOW,
  CATCH_BOARD_PRIORITY.MEDIUM,
  CATCH_BOARD_PRIORITY.HIGH,
])

export const CATCH_BOARD_PRIORITY_META = Object.freeze({
  [CATCH_BOARD_PRIORITY.LOW]: {
    key: CATCH_BOARD_PRIORITY.LOW,
    label: 'Low',
  },
  [CATCH_BOARD_PRIORITY.MEDIUM]: {
    key: CATCH_BOARD_PRIORITY.MEDIUM,
    label: 'Medium',
  },
  [CATCH_BOARD_PRIORITY.HIGH]: {
    key: CATCH_BOARD_PRIORITY.HIGH,
    label: 'High',
  },
})

export const CATCH_BOARD_LOST_REASON = Object.freeze({
  PRICE: 'price',
  TIMING: 'timing',
  COMPETITOR: 'competitor',
  NO_RESPONSE: 'no_response',
  NOT_A_FIT: 'not_a_fit',
  DUPLICATE: 'duplicate',
  OTHER: 'other',
})

export const CATCH_BOARD_LOST_REASONS = Object.freeze([
  CATCH_BOARD_LOST_REASON.PRICE,
  CATCH_BOARD_LOST_REASON.TIMING,
  CATCH_BOARD_LOST_REASON.COMPETITOR,
  CATCH_BOARD_LOST_REASON.NO_RESPONSE,
  CATCH_BOARD_LOST_REASON.NOT_A_FIT,
  CATCH_BOARD_LOST_REASON.DUPLICATE,
  CATCH_BOARD_LOST_REASON.OTHER,
])

export const CATCH_BOARD_LOST_REASON_META = Object.freeze({
  [CATCH_BOARD_LOST_REASON.PRICE]: {
    key: CATCH_BOARD_LOST_REASON.PRICE,
    label: 'Price',
  },
  [CATCH_BOARD_LOST_REASON.TIMING]: {
    key: CATCH_BOARD_LOST_REASON.TIMING,
    label: 'Timing',
  },
  [CATCH_BOARD_LOST_REASON.COMPETITOR]: {
    key: CATCH_BOARD_LOST_REASON.COMPETITOR,
    label: 'Competitor',
  },
  [CATCH_BOARD_LOST_REASON.NO_RESPONSE]: {
    key: CATCH_BOARD_LOST_REASON.NO_RESPONSE,
    label: 'No response',
  },
  [CATCH_BOARD_LOST_REASON.NOT_A_FIT]: {
    key: CATCH_BOARD_LOST_REASON.NOT_A_FIT,
    label: 'Not a fit',
  },
  [CATCH_BOARD_LOST_REASON.DUPLICATE]: {
    key: CATCH_BOARD_LOST_REASON.DUPLICATE,
    label: 'Duplicate',
  },
  [CATCH_BOARD_LOST_REASON.OTHER]: {
    key: CATCH_BOARD_LOST_REASON.OTHER,
    label: 'Other',
  },
})

export const CATCH_BOARD_LEAD_SOURCE = Object.freeze({
  CROWS_NEST: 'crows_nest',
  SPARTACUS: 'spartacus',
  INBOUND: 'inbound',
  REFERRAL: 'referral',
  COLD_OUTREACH: 'cold_outreach',
  EVENT: 'event',
  OTHER: 'other',
})

export const CATCH_BOARD_LEAD_SOURCES = Object.freeze([
  CATCH_BOARD_LEAD_SOURCE.CROWS_NEST,
  CATCH_BOARD_LEAD_SOURCE.SPARTACUS,
  CATCH_BOARD_LEAD_SOURCE.INBOUND,
  CATCH_BOARD_LEAD_SOURCE.REFERRAL,
  CATCH_BOARD_LEAD_SOURCE.COLD_OUTREACH,
  CATCH_BOARD_LEAD_SOURCE.EVENT,
  CATCH_BOARD_LEAD_SOURCE.OTHER,
])

export const CATCH_BOARD_LEAD_SOURCE_META = Object.freeze({
  [CATCH_BOARD_LEAD_SOURCE.CROWS_NEST]: {
    key: CATCH_BOARD_LEAD_SOURCE.CROWS_NEST,
    label: "Crow's Nest",
  },
  [CATCH_BOARD_LEAD_SOURCE.SPARTACUS]: {
    key: CATCH_BOARD_LEAD_SOURCE.SPARTACUS,
    label: 'Spartacus',
  },
  [CATCH_BOARD_LEAD_SOURCE.INBOUND]: {
    key: CATCH_BOARD_LEAD_SOURCE.INBOUND,
    label: 'Inbound',
  },
  [CATCH_BOARD_LEAD_SOURCE.REFERRAL]: {
    key: CATCH_BOARD_LEAD_SOURCE.REFERRAL,
    label: 'Referral',
  },
  [CATCH_BOARD_LEAD_SOURCE.COLD_OUTREACH]: {
    key: CATCH_BOARD_LEAD_SOURCE.COLD_OUTREACH,
    label: 'Cold outreach',
  },
  [CATCH_BOARD_LEAD_SOURCE.EVENT]: {
    key: CATCH_BOARD_LEAD_SOURCE.EVENT,
    label: 'Event',
  },
  [CATCH_BOARD_LEAD_SOURCE.OTHER]: {
    key: CATCH_BOARD_LEAD_SOURCE.OTHER,
    label: 'Other',
  },
})

export const CATCH_BOARD_DEFAULT_STAGE = CATCH_BOARD_STAGE.CASTING
export const CATCH_BOARD_DEFAULT_PRIORITY = CATCH_BOARD_PRIORITY.MEDIUM

/** Field length limits — keep aligned with express_app CatchBoardDeal model. */
export const CATCH_BOARD_DEAL_NAME_MAX_LENGTH = 200
export const CATCH_BOARD_COMPANY_NAME_MAX_LENGTH = 200
export const CATCH_BOARD_DESCRIPTION_MAX_LENGTH = 5000
export const CATCH_BOARD_LEAD_SOURCE_DETAIL_MAX_LENGTH = 200
export const CATCH_BOARD_LOST_REASON_NOTE_MAX_LENGTH = 1000

/**
 * Exclusive “who acts next” signal. Unset (`null`) means not triaged.
 * Must stay aligned with express_app/constants/catchBoard.js.
 */
export const CATCH_BOARD_READINESS = Object.freeze({
  SALES_READY: 'sales_ready',
  CUSTOMER_SUCCESS: 'customer_success',
  PRODUCT: 'product',
})

export const CATCH_BOARD_READINESSES = Object.freeze([
  CATCH_BOARD_READINESS.SALES_READY,
  CATCH_BOARD_READINESS.CUSTOMER_SUCCESS,
  CATCH_BOARD_READINESS.PRODUCT,
])

export const CATCH_BOARD_READINESS_META = Object.freeze({
  [CATCH_BOARD_READINESS.SALES_READY]: {
    key: CATCH_BOARD_READINESS.SALES_READY,
    cardLabel: 'Sales ready',
    label: 'Ready for sales contact',
  },
  [CATCH_BOARD_READINESS.CUSTOMER_SUCCESS]: {
    key: CATCH_BOARD_READINESS.CUSTOMER_SUCCESS,
    cardLabel: 'CS needed',
    label: 'Customer success needed',
  },
  [CATCH_BOARD_READINESS.PRODUCT]: {
    key: CATCH_BOARD_READINESS.PRODUCT,
    cardLabel: 'Product needed',
    label: 'Product improvements needed',
  },
})

export const CATCH_BOARD_DEFAULT_READINESS = null

export const CATCH_BOARD_READINESS_NOTE_MAX_LENGTH = 1000

/** Linked venues — keep aligned with express_app CatchBoardDeal model. */
export const CATCH_BOARD_MAX_LINKED_VENUES = 50
export const CATCH_BOARD_MAX_LINKED_OPPORTUNITIES = 20
export const CATCH_BOARD_VENUE_SEARCH_MIN_CHARS = 2
export const CATCH_BOARD_VENUE_SEARCH_PAGE_SIZE = 8
export const CATCH_BOARD_VENUE_SEARCH_DEBOUNCE_MS = 300

/** Board list search — commit as you type (Enter / clear still commit immediately). */
export const CATCH_BOARD_DEAL_SEARCH_DEBOUNCE_MS = 300

/** Buyers — keep aligned with express_app CatchBoardDeal model. */
export const CATCH_BOARD_MAX_BUYERS_PER_ROLE = 20
export const CATCH_BOARD_BUYER_NAME_MAX_LENGTH = 200
export const CATCH_BOARD_BUYER_EMAIL_MAX_LENGTH = 320
export const CATCH_BOARD_BUYER_ROLE_MAX_LENGTH = 120
export const CATCH_BOARD_BUYER_PHONE_MAX_LENGTH = 40

export const CATCH_BOARD_BUYER_ROLE_KEY = Object.freeze({
  ECONOMIC: 'economicBuyers',
  USER: 'userBuyers',
})

export const CATCH_BOARD_BUYER_ROLE_META = Object.freeze({
  [CATCH_BOARD_BUYER_ROLE_KEY.ECONOMIC]: {
    key: CATCH_BOARD_BUYER_ROLE_KEY.ECONOMIC,
    label: 'Economic buyers',
    hint: 'Budget holders / decision-makers who sign off spend.',
  },
  [CATCH_BOARD_BUYER_ROLE_KEY.USER]: {
    key: CATCH_BOARD_BUYER_ROLE_KEY.USER,
    label: 'User buyers',
    hint: 'Day-to-day users who champion or use the product.',
  },
})

/** Board ownership filter (shared team board). */
export const CATCH_BOARD_OWNER_FILTER = Object.freeze({
  MINE: 'mine',
  ALL: 'all',
  UNASSIGNED: 'unassigned',
})

export const CATCH_BOARD_OWNER_FILTERS = Object.freeze([
  CATCH_BOARD_OWNER_FILTER.MINE,
  CATCH_BOARD_OWNER_FILTER.ALL,
  CATCH_BOARD_OWNER_FILTER.UNASSIGNED,
])

export const CATCH_BOARD_OWNER_FILTER_META = Object.freeze({
  [CATCH_BOARD_OWNER_FILTER.MINE]: {
    key: CATCH_BOARD_OWNER_FILTER.MINE,
    label: 'My deals',
  },
  [CATCH_BOARD_OWNER_FILTER.ALL]: {
    key: CATCH_BOARD_OWNER_FILTER.ALL,
    label: 'All deals',
  },
  [CATCH_BOARD_OWNER_FILTER.UNASSIGNED]: {
    key: CATCH_BOARD_OWNER_FILTER.UNASSIGNED,
    label: 'Unassigned',
  },
})

export const CATCH_BOARD_DEFAULT_OWNER_FILTER = CATCH_BOARD_OWNER_FILTER.MINE

/**
 * Board readiness queue filter.
 * `not_triaged` maps to API `readiness=not_triaged` (unset / null deals).
 */
export const CATCH_BOARD_READINESS_FILTER = Object.freeze({
  ALL: 'all',
  SALES_READY: CATCH_BOARD_READINESS.SALES_READY,
  CUSTOMER_SUCCESS: CATCH_BOARD_READINESS.CUSTOMER_SUCCESS,
  PRODUCT: CATCH_BOARD_READINESS.PRODUCT,
  NOT_TRIAGED: 'not_triaged',
})

export const CATCH_BOARD_READINESS_FILTERS = Object.freeze([
  CATCH_BOARD_READINESS_FILTER.ALL,
  CATCH_BOARD_READINESS_FILTER.SALES_READY,
  CATCH_BOARD_READINESS_FILTER.CUSTOMER_SUCCESS,
  CATCH_BOARD_READINESS_FILTER.PRODUCT,
  CATCH_BOARD_READINESS_FILTER.NOT_TRIAGED,
])

export const CATCH_BOARD_READINESS_FILTER_META = Object.freeze({
  [CATCH_BOARD_READINESS_FILTER.ALL]: {
    key: CATCH_BOARD_READINESS_FILTER.ALL,
    label: 'All',
  },
  [CATCH_BOARD_READINESS_FILTER.SALES_READY]: {
    key: CATCH_BOARD_READINESS_FILTER.SALES_READY,
    label: CATCH_BOARD_READINESS_META[CATCH_BOARD_READINESS.SALES_READY].cardLabel,
  },
  [CATCH_BOARD_READINESS_FILTER.CUSTOMER_SUCCESS]: {
    key: CATCH_BOARD_READINESS_FILTER.CUSTOMER_SUCCESS,
    label:
      CATCH_BOARD_READINESS_META[CATCH_BOARD_READINESS.CUSTOMER_SUCCESS].cardLabel,
  },
  [CATCH_BOARD_READINESS_FILTER.PRODUCT]: {
    key: CATCH_BOARD_READINESS_FILTER.PRODUCT,
    label: CATCH_BOARD_READINESS_META[CATCH_BOARD_READINESS.PRODUCT].cardLabel,
  },
  [CATCH_BOARD_READINESS_FILTER.NOT_TRIAGED]: {
    key: CATCH_BOARD_READINESS_FILTER.NOT_TRIAGED,
    label: 'Not triaged',
  },
})

export const CATCH_BOARD_DEFAULT_READINESS_FILTER =
  CATCH_BOARD_READINESS_FILTER.ALL

/** Must stay aligned with `BRIDGE_DEAL_QUERY` in `src/constants/bridge`. */
export const CATCH_BOARD_DEAL_QUERY = 'deal'

export function isCatchBoardStage(value) {
  return CATCH_BOARD_STAGES.includes(value)
}

export function isCatchBoardClosedStage(value) {
  return CATCH_BOARD_CLOSED_STAGES.includes(value)
}

export function isCatchBoardOpenStage(value) {
  return CATCH_BOARD_OPEN_STAGES.includes(value)
}

export function isCatchBoardPriority(value) {
  return CATCH_BOARD_PRIORITIES.includes(value)
}

export function isCatchBoardLostReason(value) {
  return CATCH_BOARD_LOST_REASONS.includes(value)
}

export function isCatchBoardLeadSource(value) {
  return CATCH_BOARD_LEAD_SOURCES.includes(value)
}

export function isCatchBoardReadiness(value) {
  return CATCH_BOARD_READINESSES.includes(value)
}

export function getCatchBoardStageMeta(stage) {
  return CATCH_BOARD_STAGE_META[stage] || null
}

export function getCatchBoardStageLabel(stage, { includeEmoji = false } = {}) {
  const meta = getCatchBoardStageMeta(stage)
  if (!meta) {
    return stage || ''
  }

  const text = meta.subtitle ? `${meta.label} / ${meta.subtitle}` : meta.label
  return includeEmoji && meta.emoji ? `${meta.emoji} ${text}` : text
}

export function getCatchBoardPriorityLabel(priority) {
  return CATCH_BOARD_PRIORITY_META[priority]?.label || priority || ''
}

export function getCatchBoardLostReasonLabel(reason) {
  return CATCH_BOARD_LOST_REASON_META[reason]?.label || reason || ''
}

export function getCatchBoardLeadSourceLabel(source) {
  return CATCH_BOARD_LEAD_SOURCE_META[source]?.label || source || ''
}

export function getCatchBoardReadinessMeta(readiness) {
  return CATCH_BOARD_READINESS_META[readiness] || null
}

export function getCatchBoardReadinessLabel(readiness) {
  return CATCH_BOARD_READINESS_META[readiness]?.label || readiness || ''
}

export function getCatchBoardReadinessCardLabel(readiness) {
  return CATCH_BOARD_READINESS_META[readiness]?.cardLabel || readiness || ''
}

export function lostReasonRequiresNote(reason) {
  return reason === CATCH_BOARD_LOST_REASON.OTHER
}

export function readinessRequiresNote(readiness) {
  return (
    readiness === CATCH_BOARD_READINESS.CUSTOMER_SUCCESS ||
    readiness === CATCH_BOARD_READINESS.PRODUCT
  )
}

/** Must stay aligned with express_app CatchBoardDeal activity types. */
export const CATCH_BOARD_ACTIVITY_TYPE = Object.freeze({
  DEAL_CREATED: 'deal_created',
  DEAL_UPDATED: 'deal_updated',
  STAGE_CHANGED: 'stage_changed',
  OWNER_CHANGED: 'owner_changed',
  VALUE_CHANGED: 'value_changed',
  NOTE_ADDED: 'note_added',
  VENUE_LINKED: 'venue_linked',
  VENUE_UNLINKED: 'venue_unlinked',
  BUYER_UPDATED: 'buyer_updated',
  WON_RECORDED: 'won_recorded',
  LOST_RECORDED: 'lost_recorded',
  DEAL_DELETED: 'deal_deleted',
  READINESS_CHANGED: 'readiness_changed',
})

export const CATCH_BOARD_ACTIVITY_TYPES = Object.freeze(
  Object.values(CATCH_BOARD_ACTIVITY_TYPE),
)

export const CATCH_BOARD_ACTIVITY_TYPE_META = Object.freeze({
  [CATCH_BOARD_ACTIVITY_TYPE.DEAL_CREATED]: { label: 'Created' },
  [CATCH_BOARD_ACTIVITY_TYPE.DEAL_UPDATED]: { label: 'Updated' },
  [CATCH_BOARD_ACTIVITY_TYPE.STAGE_CHANGED]: { label: 'Stage' },
  [CATCH_BOARD_ACTIVITY_TYPE.OWNER_CHANGED]: { label: 'Owner' },
  [CATCH_BOARD_ACTIVITY_TYPE.VALUE_CHANGED]: { label: 'Value' },
  [CATCH_BOARD_ACTIVITY_TYPE.NOTE_ADDED]: { label: 'Note' },
  [CATCH_BOARD_ACTIVITY_TYPE.VENUE_LINKED]: { label: 'Venue linked' },
  [CATCH_BOARD_ACTIVITY_TYPE.VENUE_UNLINKED]: { label: 'Venue unlinked' },
  [CATCH_BOARD_ACTIVITY_TYPE.BUYER_UPDATED]: { label: 'Buyers' },
  [CATCH_BOARD_ACTIVITY_TYPE.WON_RECORDED]: { label: 'Landed' },
  [CATCH_BOARD_ACTIVITY_TYPE.LOST_RECORDED]: { label: 'Got away' },
  [CATCH_BOARD_ACTIVITY_TYPE.DEAL_DELETED]: { label: 'Deleted' },
  [CATCH_BOARD_ACTIVITY_TYPE.READINESS_CHANGED]: { label: 'Readiness' },
})

export function getCatchBoardActivityTypeLabel(type) {
  return CATCH_BOARD_ACTIVITY_TYPE_META[type]?.label || type || 'Activity'
}

/**
 * Timeline summary copy — drop redundant “Readiness” wording when the tag
 * already says Readiness.
 * @param {{ type?: string, summary?: string }} [entry]
 */
export function formatCatchBoardActivitySummary(entry = {}) {
  const summary = String(entry.summary || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!summary) {
    return 'Activity'
  }

  if (entry.type !== CATCH_BOARD_ACTIVITY_TYPE.READINESS_CHANGED) {
    return summary
  }

  if (summary.startsWith('Readiness: ')) {
    return summary.slice('Readiness: '.length).trim() || summary
  }

  if (summary === 'Updated readiness note') {
    return 'Updated note'
  }

  if (summary.startsWith('Updated readiness note:')) {
    const rest = summary.slice('Updated readiness note:'.length).trim()
    return rest ? `Updated note: ${rest}` : 'Updated note'
  }

  return summary
}
